import React, { useEffect, useMemo, useRef, useState } from "react";
import { EMPLOYEE_STATUS_OPTIONS, UI } from "../directory/employeeStyles";
import { statusLabelFromRaw } from "../../../../utils/userStatus";

function normalizeStatus(raw) {
  return statusLabelFromRaw(raw);
}

function toInputValue(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function parseRateOrNull(str) {
  const s = String(str ?? "").trim();
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? undefined : n;
}

function getInitials(name) {
  return (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase();
}

const STATUS_COLOR = { saving: "#94a3b8", saved: "#15803d", error: "#b91c1c" };
const STATUS_LABEL = { saving: "Saving…", saved: "Auto-saved", error: "Save failed" };

export default function EmployeePayRate({ employees, loading, onRefresh, onUpdateRates }) {
  const [searchTerm,   setSearchTerm]   = useState("");
  const [filterRole,   setFilterRole]   = useState("tutor");
  const [filterStatus, setFilterStatus] = useState("Active");
  const [drafts,       setDrafts]       = useState({});
  const [savingByUid,  setSavingByUid]  = useState({});
  const [saveStatus,   setSaveStatus]   = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const statusTimerRef = useRef(null);

  /* Seed drafts when employees load (don't overwrite in-progress edits) */
  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      (employees || []).forEach((e) => {
        if (!next[e.id]) {
          next[e.id] = {
            teachingRate: toInputValue(e.teachingRate),
            adminRate:    toInputValue(e.adminRate),
          };
        }
      });
      return next;
    });
  }, [employees]);

  const filtered = useMemo(() => {
    const term       = searchTerm.trim().toLowerCase();
    const filterNorm = filterStatus.trim().toLowerCase();
    const roleNorm   = filterRole.trim().toLowerCase();

    return (employees || [])
      .filter((e) => {
        const eRole = (e.role || "tutor").toLowerCase();
        if (roleNorm !== "all" && eRole !== roleNorm) return false;
        const statusNorm = normalizeStatus(e.status).toLowerCase();
        if (term && !String(e.name || "").toLowerCase().includes(term)) return false;
        if (filterNorm !== "all" && statusNorm !== filterNorm) return false;
        return true;
      })
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [employees, searchTerm, filterStatus, filterRole]);

  function setDraftField(uid, field, val) {
    setDrafts((p) => ({
      ...p,
      [uid]: { ...(p[uid] || { teachingRate: "", adminRate: "" }), [field]: val },
    }));
  }

  /* Auto-save: triggered on input blur */
  async function commit(uid) {
    const emp = (employees || []).find((x) => x.id === uid);
    if (!emp) return;

    const d = drafts[uid] || { teachingRate: "", adminRate: "" };
    const tParsed = parseRateOrNull(d.teachingRate);
    const aParsed = parseRateOrNull(d.adminRate);

    if (tParsed === undefined || aParsed === undefined) {
      setSaveStatus("error");
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
      statusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 3000);
      setDrafts((p) => ({
        ...p,
        [uid]: { teachingRate: toInputValue(emp.teachingRate), adminRate: toInputValue(emp.adminRate) },
      }));
      return;
    }

    const patch = {};
    if ((emp.teachingRate ?? null) !== (tParsed === null ? null : tParsed)) patch.teachingRate = tParsed;
    if ((emp.adminRate    ?? null) !== (aParsed === null ? null : aParsed)) patch.adminRate    = aParsed;
    if (!Object.keys(patch).length) return;

    setSaveStatus("saving");
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setSavingByUid((p) => ({ ...p, [uid]: true }));

    try {
      await onUpdateRates(uid, patch);
      setSaveStatus("saved");
      statusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2500);
    } catch (e) {
      console.error(e);
      setSaveStatus("error");
      statusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 3000);
      setDrafts((p) => ({
        ...p,
        [uid]: { teachingRate: toInputValue(emp.teachingRate), adminRate: toInputValue(emp.adminRate) },
      }));
    } finally {
      setSavingByUid((p) => ({ ...p, [uid]: false }));
    }
  }

  return (
    <div style={UI.prPage}>
      {/* Page header */}
      <div style={UI.pageHeader}>
        <div>
          <div style={UI.pageTitle}>Pay Rates</div>
          {!loading && (
            <div style={UI.pageSubtitle}>
              {filtered.length} {filtered.length === 1 ? "employee" : "employees"}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Auto-save status */}
          {saveStatus !== "idle" && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              fontWeight: 500,
              color: STATUS_COLOR[saveStatus],
              transition: "opacity 0.2s",
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: STATUS_COLOR[saveStatus],
                flexShrink: 0,
                animation: saveStatus === "saving" ? "pulse 1s infinite" : "none",
              }} />
              {STATUS_LABEL[saveStatus]}
            </div>
          )}
          <button style={UI.iconBtn} onClick={onRefresh}>
            <span style={{ fontSize: 14 }}>↻</span> Refresh
          </button>
        </div>
      </div>

      {/* Controls */}
      <div style={UI.controlsBar}>
        <div style={UI.searchWrap}>
          <input
            style={UI.searchInput}
            placeholder="Search by name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button style={UI.searchClear} onClick={() => setSearchTerm("")}>×</button>
          )}
        </div>

        <select style={UI.select} value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
          <option value="tutor">Tutors</option>
          <option value="admin">Admins</option>
          <option value="all">All Roles</option>
        </select>

        <select style={UI.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          {EMPLOYEE_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Rate list */}
      <div style={UI.prListCard}>
        {/* Column labels */}
        <div style={colHeaderStyle}>
          <div style={{ flex: "1 1 180px", ...colLabelStyle }}>Employee</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ width: 130, ...colLabelStyle }}>Teaching Rate</div>
            <div style={{ width: 130, ...colLabelStyle }}>Admin Rate</div>
          </div>
        </div>

        <div style={UI.prListScroll}>
          {loading ? (
            <div style={UI.emptyState}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={UI.emptyState}>No employees found.</div>
          ) : (
            filtered.map((e, idx) => {
              const status    = normalizeStatus(e.status);
              const statusKey = status.toLowerCase();
              const d         = drafts[e.id] || { teachingRate: "", adminRate: "" };
              const isLast    = idx === filtered.length - 1;

              return (
                <div
                  key={e.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "11px 20px",
                    gap: 16,
                    flexWrap: "wrap",
                    borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                  }}
                >
                  {/* Employee info */}
                  <div style={{ display: "flex", alignItems: "center", gap: 11, flex: "1 1 180px", minWidth: 0 }}>
                    <div style={UI.avatarCircle}>{getInitials(e.name)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {e.name || "—"}
                      </div>
                      <div style={{ marginTop: 3 }}>
                        <span style={{ ...UI.statusBadge(statusKey), display: "inline-flex" }}>
                          <span style={UI.statusDot(statusKey)} />
                          {status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Rate inputs — auto-save on blur */}
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <RateInput
                      value={d.teachingRate}
                      onChange={(v) => setDraftField(e.id, "teachingRate", v)}
                      onBlur={() => commit(e.id)}
                      disabled={!!savingByUid[e.id]}
                    />
                    <RateInput
                      value={d.adminRate}
                      onChange={(v) => setDraftField(e.id, "adminRate", v)}
                      onBlur={() => commit(e.id)}
                      disabled={!!savingByUid[e.id]}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}

function RateInput({ value, onChange, onBlur, disabled }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      width: 130,
      border: `1px solid ${disabled ? "#f1f5f9" : "#e5e7eb"}`,
      borderRadius: 8,
      overflow: "hidden",
      background: disabled ? "#fafafa" : "#fff",
      transition: "border-color 0.15s",
    }}>
      <span style={{
        padding: "7px 7px 7px 10px",
        fontSize: 13,
        color: "#94a3b8",
        background: "transparent",
        flexShrink: 0,
        fontWeight: 400,
      }}>$</span>
      <input
        type="text"
        inputMode="decimal"
        placeholder="0.00"
        value={value}
        disabled={disabled}
        onChange={(ev) => {
          const v = ev.target.value;
          if (v === "" || /^[0-9]*\.?[0-9]*$/.test(v)) onChange(v);
        }}
        onBlur={onBlur}
        style={{
          border: "none",
          outline: "none",
          padding: "7px 4px",
          fontSize: 14,
          color: "#0f172a",
          width: 60,
          background: "transparent",
          fontFamily: "inherit",
          fontWeight: 400,
        }}
      />
      <span style={{
        padding: "7px 8px 7px 0",
        fontSize: 11,
        color: "#cbd5e1",
        fontWeight: 400,
        flexShrink: 0,
      }}>/hr</span>
    </div>
  );
}

const colHeaderStyle = {
  display: "flex",
  alignItems: "center",
  padding: "8px 20px",
  borderBottom: "1px solid #f1f5f9",
  gap: 16,
  flexWrap: "wrap",
};

const colLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};
